import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import ChatBot, { Button } from 'react-chatbotify';
import MarkdownRenderer from '@rcb-plugins/markdown-renderer';
import { nanoid } from 'nanoid';
import Loader from 'components/core/Loader';
import Main from 'pages/Main';
import { Store, Gateway, BinDiscount, PaymentChannels, SubscriptionDetails, BillingInvoices, ViewBillingInvoiceDetails } from 'pages/Setting';
import { Plan, Product, Invoice, Subscription, ViewSubscriptionDetails, Customer, ViewProductDetails, ViewInvoiceDetails, ViewPlanDetails, ViewCustomerDetails } from 'pages/Subscriptions';
import { Meter } from 'pages/BillingEngine';
import { PricingModel } from 'pages/BillingEngine/pricingModel';
import ViewMeterDetails from 'pages/BillingEngine/meter/viewMeterDetails';
import ViewPricingModelDetails from 'pages/BillingEngine/pricingModel/viewPricingModelDetails';
import ViewTransection from 'pages/Transaction/ViewTransection';
import ViewIntentData from 'pages/pyamentIntent/viewIntentData';
import PaymentLinkDetail from 'pages/PaymentLinks/paymentDetail';
import Dashboard from 'pages/Dashboard';
import Transaction from 'pages/Transaction';
import Login from 'pages/Login';
import ResetPassword from 'pages/Login/ResetPassword';
import CustomPayment from 'pages/PaymentLinks';
import { setUser, setBanksData, setStore } from 'app/features/userSlice';
import { getAccessToken, getRealm } from 'utils/storage';
import { identifyUser } from 'lib/hyperdx';
import PaymentIntent from 'pages/pyamentIntent';
import UserService from 'api/users';
import LinkedAccount from 'pages/linkedAccount';
import UnAuthorized from 'pages/Auth/UnAuthorized';
import { getUsersRole } from 'utils/services';
import { getCubejsToken } from 'utils/cubejs';
import { BlockList, PerformanceDashboard, Rules } from 'pages/XShield';
import XShieldWrapper from 'pages/XShield/XShieldWrapper';
import MerchantInvoice from 'pages/Invoice';
import { INVOICE_VARIANTS } from 'pages/Invoice/invoiceConfig';
import { User } from 'pages/User';
import ViewMerchantInvoiceDetails from 'pages/Invoice/viewInvoiceDetails';
import { PaymentRouting, ViewRuleDetails } from 'pages/PaymentRouting';
import { canAccessRoute } from 'utils/featureAccess';

function RequireAuth({ children }) {
  const location = useLocation();
  if (!getAccessToken() && location?.pathname !== '/auth/login') {
    return <Navigate to='/auth/login' replace={true} />;
  }
  return children;
}

function PublicRoute({ children }) {
  const location = useLocation();
  const { isLoggedIn, storesAreDisabled } = useSelector((state) => state?.company);
  if (getAccessToken() && location?.pathname !== '/auth/set-new-password' && !storesAreDisabled) {
    return <Navigate to='/dashboard' replace={true} />;
  } else if (getAccessToken() && storesAreDisabled && isLoggedIn && location?.pathname !== '/unauthorized') {
    return <Navigate to='/unauthorized' replace={true} />;
  } else if (!getAccessToken() && location?.pathname !== '/auth/login' && location?.pathname !== '/auth/forgot' && location?.pathname !== '/auth/set-new-password') {
    return <Navigate to='/auth/login' replace={true} />;
  }
  return children;
}

function App() {
  const [loading, setLoading] = useState(false);
  const [isStoreDisabled, setIStoreDisabled] = useState(false);
  const store = useSelector((state) => state?.company?.store);
  const { isPim, isSubscriptionsModuleAccessOnly } = getUsersRole();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const checkUser = async () => {
    if (getAccessToken()) {
      setLoading(true);
      try {
        const results = await Promise.allSettled([getCubejsToken(), UserService.me()]);
        const [cubeResult, userResult] = results;
        // Handle user info result
        if (userResult?.status === 'fulfilled') {
          const response = userResult.value;
          dispatch(setUser(response?.data?.data));
          const store_id = localStorage.getItem('store_id');
          const all_Store = response?.data?.data?.store;
          if (store_id && all_Store?.length > 1) {
            const selectedStoreData = all_Store?.find((store) => store?._id === store_id);
            if (selectedStoreData) {
              dispatch(setStore({ ...selectedStoreData, isSelected: true }));
            }
          }
          identifyUser({ storeId: store_id || all_Store?.[0]?._id });
        } else {
          const err = userResult?.reason;
          const reason = err?.response?.data?.error?.reason || err?.response?.error?.reason || err?.data?.error?.reason;
          if (reason === 'STORES_INACTIVE') {
            setIStoreDisabled(true);
            dispatch(
              setUser({
                storesAreDisabled: true,
                isLoggedIn: true,
                store: [],
                userStores: [],
              })
            );
            identifyUser();
            navigate('/unauthorized');
          } else {
            throw err;
          }
        }
      } catch (err) {
        console.error('checkUser unexpected error', err);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    checkUser();
    // getBanksData();
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    if (location?.state?.pathname === '/auth/login' && location?.state?.loginSuccess) {
      checkUser();
    }
  }, [location?.pathname]);
  useEffect(() => {
    const scrollContainer = document.querySelector('.main_content');
    if (scrollContainer) {
      scrollContainer.scrollTo(0, 0);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location?.pathname]);
  useEffect(() => {
    if (getAccessToken() && store?._id) {
      identifyUser({ storeId: store._id });
    }
  }, [store?._id]);

  if (loading || (getAccessToken() && !store && !isStoreDisabled)) return <Loader />;

  const flow = {
    start: {
      message: 'Hi, I am XPay Support Bot! \n \nI will support you in integration during development and resolving issues. \n \nHow may I help you today?',
      function: async (userInput) => {
        const requestOptions = {
          method: 'POST',
          redirect: 'follow',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAccessToken()}`,
          },
          body: JSON.stringify({
            message: userInput.userInput,
          }),
        };

        const getR = await fetch(`https://xpay-bot.xstak.com/chat/${getRealm()}-${nanoid(10)}`, requestOptions)
          .then((response) => response.json())
          .catch((error) => console.error(error));

        const reply = getR.reply ?? '';
        await userInput.simulateStreamMessage(reply);
        return reply;
      },
      renderMarkdown: ['BOT', 'USER'],
    },
  };

  const styles = {
    botBubbleStyle: {
      backgroundColor: '#8CCC43',
      color: '#ffffff',
    },
    userBubbleStyle: {
      backgroundColor: '#53A366',
      color: '#ffffff',
    },
    headerStyle: {
      background: '#53A366', // or #8CCC43 if you prefer
      color: '#ffffff',
    },
    footerStyle: {
      background: '#53A366',
      color: '#ffffff',
    },
    chatButtonStyle: {
      background: '#8CCC43', // closed bubble background
      color: '#ffffff', // icon/text color if applicable
      width: 55,
      height: 55,
    },
    chatIconStyle: {
      width: 40,
      height: 40,
    },
    tooltipStyle: {
      background: '#53A366',
      color: '#ffffff',
    },
  };

  const settingBot = {
    header: {
      title: 'XPay Support Bot',
      showAvatar: false,
      buttons: [Button.CLOSE_CHAT_BUTTON],
    },
    footer: {
      text: 'Powered by XPay',
      buttons: [],
    },
    emoji: {
      disabled: true,
    },
    botBubble: {
      showAvatar: false,
      // dangerouslySetInnerHtml: true,
    },
    tooltip: {
      mode: 'CLOSE',
      text: `Need Support? Let's talk`,
    },

    // chatButton: {
    //   icon: 'http://localhost:3200/XPay.svg',
    // },
  };

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path='unauthorized'
        element={
          <PublicRoute>
            <UnAuthorized />
          </PublicRoute>
        }
      />
      <Route
        path='auth/login'
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path='auth/forgot'
        element={
          <PublicRoute>
            <Login isForgetMode={true} />
          </PublicRoute>
        }
      />
      <Route
        path='auth/set-new-password'
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />
      {/* Authenticated Routes */}
      <Route
        path='*'
        element={
          <RequireAuth>
            <Main />
            <ChatBot
              plugins={[MarkdownRenderer()]}
              flow={flow}
              styles={styles}
              settings={settingBot}
              options={{ theme: { embedded: true }, chatHistory: { storageKey: 'example_simulation_stream' }, botBubble: { simStream: true, streamSpeed: 1500 }, isTypingMessage: false }}
            />
          </RequireAuth>
        }>
        {!isSubscriptionsModuleAccessOnly && (
          <>
            <Route path='dashboard' element={<Dashboard />} />
            <Route path='transactions' element={<Transaction />} />
            <Route path='transactions/:id' element={<ViewTransection />} />
            <Route path='paymentIntent' element={<PaymentIntent />} />
            <Route path='paymentIntent/:id' element={<ViewIntentData />} />
            {/* XShield */}
            <Route path='x-shield'>
              <Route
                path='block-list'
                element={
                  <XShieldWrapper>
                    <BlockList />
                  </XShieldWrapper>
                }
              />
              <Route
                path='performance'
                element={
                  <XShieldWrapper>
                    <PerformanceDashboard />
                  </XShieldWrapper>
                }
              />
              <Route
                path='rules'
                element={
                  <XShieldWrapper>
                    <Rules />
                  </XShieldWrapper>
                }
              />
            </Route>
            <Route path='billing-engine'>
              <Route path='meter' element={<Meter />} />
              <Route path='meter/:id' element={<ViewMeterDetails />} />
              <Route path='pricing-model' element={<PricingModel />} />
              <Route path='pricing-model/:id' element={<ViewPricingModelDetails />} />
            </Route>
            {/* Payment Links */}
            {canAccessRoute(store, 'Payment Links') && (
              <>
                <Route path='paymentlink' element={<CustomPayment />} />
                <Route path='paymentlink/:id' element={<PaymentLinkDetail />} />
              </>
            )}
            {/* Payment Routing */}
            {canAccessRoute(store, 'Payment Routing') && (
              <>
                <Route path='payment-routing' element={<PaymentRouting />} />
                <Route path='payment-routing/:id' element={<ViewRuleDetails />} />
              </>
            )}
            <Route path='invoice'>
              <Route index element={<Navigate to='aggregator' replace />} />
              <Route path='aggregator' element={<MerchantInvoice {...INVOICE_VARIANTS.aggregator} />} />
              <Route path='paylater' element={<MerchantInvoice {...INVOICE_VARIANTS.paylater} />} />
              <Route path='aggregator/:id' element={<ViewMerchantInvoiceDetails invoiceType='aggregator' />} />
              <Route path='paylater/:id' element={<ViewMerchantInvoiceDetails invoiceType='paylater' />} />
            </Route>
            <Route path='users' element={<User />} />
            <Route path='setting'>
              <Route path='store' element={<Store />} />
              <Route path='gateway' element={<Gateway />} />
              <Route path='subscriptionDetails' element={<SubscriptionDetails />} />
              <Route path='bin-discount' element={<BinDiscount />} />
              <Route path='payment-channels' element={<PaymentChannels />} />
              <Route path='billing' element={<BillingInvoices />} />
              <Route path='billing/:id' element={<ViewBillingInvoiceDetails />} />
            </Route>
            <Route path='linked_account' element={<LinkedAccount />} />
          </>
        )}
        {/* Subscriptions */}
        {canAccessRoute(store, 'Subscriptions') && (
          <Route path='subscriptions'>
            <Route path='*' element={<Subscription />} />
            <Route path=':id' element={<ViewSubscriptionDetails />} />
            {!isPim && (
              <>
                <Route path='plan' element={<Plan />} />
                <Route path='product' element={<Product />} />
                <Route path='invoice' element={<Invoice />} />
                <Route path='customer' element={<Customer />} />
                <Route path='customer/:id' element={<ViewCustomerDetails />} />
                <Route path='product/:id' element={<ViewProductDetails />} />
                <Route path='plan/:id' element={<ViewPlanDetails />} />
                <Route path='invoice/:id' element={<ViewInvoiceDetails />} />
              </>
            )}
          </Route>
        )}
        <Route path='*' element={<Navigate to={isSubscriptionsModuleAccessOnly ? 'subscriptions' : 'dashboard'} />} />
      </Route>
    </Routes>
  );
}

export default App;
